import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import Checkbox from 'components/Checkbox';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import { EAppointmentFormStatus, IForm } from 'modules/forms/interfaces/form.interfaces';
import { setFormData } from 'modules/forms/actions/form.actions';
import { infectedInsectBitesRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';

const Assessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      statements: stateForm.statements,
    },
    mode: 'onSubmit',
  });
  const { control, watch, setValue, handleSubmit } = methods;
  const statements = watch('statements') || [];

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    dispatch(
      setFormData({
        ...values,
        status:
          values.statements?.includes('4') ||
          values.statements?.length === 1 ||
          values.statements?.length === 2
            ? EAppointmentFormStatus.NEEDS_REVIEW
            : EAppointmentFormStatus.COMPLETED,
      }),
    );
    history.push({
      pathname: infectedInsectBitesRoutes.secondaryAssessment,
      search: history.location.search,
    });
  };

  const onChangeValue = (name: string, e: ChangeEvent<HTMLInputElement>) => {
    setValue(
      'statements',
      e.target.checked
        ? [...statements, name].filter(item => item !== '4')
        : statements.filter(item => item !== name),
    );
  };

  return (
    <Page>
      <PageHeader step={3} maxSteps={5} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="Assessment"
                description="Please select all that apply to the patient:"
              >
                <Controller
                  name="statements"
                  control={control}
                  render={() => (
                    <FormGroup>
                      <Checkbox
                        id="1"
                        name="1"
                        checked={statements?.includes('1')}
                        onChange={e => onChangeValue('1', e)}
                        label="The patient believes the infected area was likely caused by an Insect Bite"
                      />
                      <Checkbox
                        id="2"
                        name="2"
                        checked={statements?.includes('2')}
                        onChange={e => onChangeValue('2', e)}
                        label="The Insect Bite was NOT caused by a Tick"
                      />
                      <Checkbox
                        id="3"
                        name="3"
                        checked={statements?.includes('3')}
                        onChange={e => onChangeValue('3', e)}
                        label="The Insect Bite occurred in the United Kingdom at least 48 hours ago"
                      />
                      <Checkbox
                        id="4"
                        name="4"
                        checked={statements?.includes('4')}
                        onChange={e => setValue('statements', e.target.checked ? ['4'] : [])}
                        label="None of the Above"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={!statements?.length}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default Assessment;
